import { Button, Dialog, FormControl, FormControlLabel, FormLabel, InputAdornment, InputLabel, MenuItem, RadioGroup, Radio, Select, Switch, TextField, CircularProgress } from "@mui/material"
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import AutocompleteField from "elements/AutocompleteField";
import { ApiRequest } from "GlobalFunctions";
import LoadingButton from "@mui/lab/LoadingButton";
import * as pdfjsLib from "pdfjs-dist";
import { BsFiletypePdf } from "react-icons/bs";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

function PdfThumb({ src }: { src: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setError(false);
    (async () => {
      try {
        const pdf = await (pdfjsLib as any).getDocument({ url: src, withCredentials: false }).promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const vp = page.getViewport({ scale: 1 });
        const scale = (canvas.parentElement?.clientWidth || 64) / vp.width;
        const scaled = page.getViewport({ scale });
        canvas.width = scaled.width; canvas.height = scaled.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport: scaled }).promise;
      } catch { if (!cancelled) setError(true); }
    })();
    return () => { cancelled = true; };
  }, [src]);
  if (error) return <BsFiletypePdf size={32} color="#135095" style={{ opacity: 0.7 }} />;
  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
}



const SPECIALIST_PROCEDURES = [
  { skill_id: 1, label: 'Curación simple',   description: 'Abrasiones, cortes, heridas superficiales' },
  { skill_id: 2, label: 'Curación avanzada', description: 'Úlceras, heridas infectadas o complejas' },
  { skill_id: 3, label: 'Ostomías',          description: 'Cambio de placa' },
  { skill_id: 4, label: 'Terapia VAC',       description: 'Instalación, recambio' },
];
const GENERAL_PROCEDURES = [
  { skill_id: 5,  label: 'Curación simple',        description: 'Limpieza y cobertura de heridas básicas' },
  { skill_id: 6,  label: 'Retiro de suturas',       description: 'Retiro de puntos y grapas' },
  { skill_id: 7,  label: 'Tratamiento inyectable',  description: 'Administración intramuscular o subcutánea' },
  { skill_id: 8,  label: 'Vía venosa',              description: 'Sueroterapia y medicamentos endovenosos' },
  { skill_id: 9,  label: 'Sonda urinaria',          description: 'Instalación y recambio de sonda Foley' },
  { skill_id: 10, label: 'Sonda digestiva',         description: 'Instalación y manejo de sonda nasogástrica' },
];
const ALL_PROCEDURES = [...SPECIALIST_PROCEDURES, ...GENERAL_PROCEDURES];

const buildDefaultTreatments = () => {
  const t: Record<string, any> = {};
  ALL_PROCEDURES.forEach(p => { t[p.skill_id] = { active: false, price: '' }; });
  return t;
};
const defaultTreatments = buildDefaultTreatments();

export default function UsersEditor(props : any) {

  /*=============================================
  VARIABLES FROM PROPS
  ============================================= */
  const {
    openEditor,
    setOpenEditor,
    user_id,
    loadList,
  } = props;

  /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
  const { enqueueSnackbar } = useSnackbar();
  /*============================================= */



  const defaultValues: any = {
    user_id: user_id ? user_id : "null",
    name: "",
    lastname: "",
    email: "",
    password: "",
    role_id: 2,
    phone: "",
    whatsApp: "",
    is_active: true,
    deleted: false,
    image: "",
    nurse_id: null,
    rut: "",
    university_degree: "",
    service: "",
    price: "",
    region_id: "",
    locations: [],
    skills: [],
    advanced_wound_care: false,
    online_advice: false,
    about_me: "",
    average_rating: "0.0000",
    locations_labels: [],
    skills_labels: [],
    is_register_approved: 0,
    plan_id: "",
    plan_expiry_date: "",
    treatments: defaultTreatments,
    home_location: "",
    sup_salud_registration: "",
    plan_type: "basico",
  };
  
  const [formValues, setFormValues] = useState(defaultValues)
  const [loading, setLoading] = useState(false);

  const [credentials, setCredentials] = useState<any[]>([]);
  const [credTitle, setCredTitle] = useState('');
  const [credFile, setCredFile] = useState<any>(null);
  const [credLoading, setCredLoading] = useState(false);
  const [preferenteTerms, setPreferenteTerms] = useState('');

  const loadCredentials = async (nurse_id: any) => {
    await ApiRequest({
      url: '/nurses/credentials',
      method: 'GET',
      query: { nurse_id },
      setResponse: (r: any) => { if (r?.status === 'success') setCredentials(r.items || []); },
    });
  };

  const addCredential = async () => {
    if (!credFile || !credTitle.trim()) { enqueueSnackbar('Agrega título y archivo', { variant: 'warning' }); return; }
    if ((credentials?.length || 0) >= 3) { enqueueSnackbar('Máximo 3 certificados', { variant: 'warning' }); return; }
    setCredLoading(true);
    const fd = new FormData();
    fd.append('nurse_id', formValues.nurse_id);
    fd.append('title', credTitle);
    fd.append('file', credFile);
    await ApiRequest({
      url: '/nurses/credentials', method: 'POST', formData: true, body: fd,
      setResponse: (r: any) => {
        setCredLoading(false);
        if (r?.status === 'success') {
          enqueueSnackbar('Certificado guardado', { variant: 'success' });
          setCredTitle(''); setCredFile(null);
          loadCredentials(formValues.nurse_id);
        }
      },
    });
  };

  const toggleVisibility = async (credential_id: number, is_visible: boolean) => {
    setCredentials(prev => prev.map(c => c.credential_id === credential_id ? { ...c, is_visible: is_visible ? 1 : 0 } : c));
    await ApiRequest({ url: '/nurses/credentials/toggle', method: 'POST', body: { credential_id, is_visible }, setResponse: () => {} });
  };

  const deleteCredential = async (credential_id: any) => {
    await ApiRequest({
      url: '/nurses/credentials/delete', method: 'POST', body: { credential_id },
      setResponse: (r: any) => { if (r?.status === 'success') loadCredentials(formValues.nurse_id); },
    });
  };





  useEffect(() => {
    ApiRequest({
      url: '/settings/details',
      method: 'GET',
      setResponse: (r: any) => { if (r.seo?.plan_preferente_terms) setPreferenteTerms(r.seo.plan_preferente_terms); }
    });
  }, []);

  /*=======================================
  GET USER DETAILS IF USER_ID EXISTS
  =======================================*/
  useEffect(() => {
    if (user_id) {
      ApiRequest({
        url: "/users/details",
        method: "GET",
        query: { user_id },
        setResponse: (response: any) => {
          setFormValues({
            ...defaultValues,
            ...response,
            treatments: { ...defaultTreatments, ...(response.treatments || {}) },
          });
          if (response.nurse_id) loadCredentials(response.nurse_id);
        },
      });
    }
    else{
      setFormValues(defaultValues);
      setCredentials([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  } , [user_id])




  const updateTreatment = (skill_id: number, field: string, value: any) => {
    const treatments = formValues.treatments || buildDefaultTreatments();
    setFormValues({
      ...formValues,
      treatments: {
        ...treatments,
        [skill_id]: { ...(treatments[skill_id] || {}), [field]: value }
      }
    });
  };

  /*=======================================
  HANDLE INPUT CHANGE
  =======================================*/
  const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };


  /*=======================================
  SUBMIT FUNCTION
  =======================================*/
  const handleSubmit = () => {  
    setLoading(true);

    const body = new FormData();
    body.append("formValues", JSON.stringify(formValues));
    console.log("formValues", formValues);


    ApiRequest({
      url: "/users/updateAccount",
      method: "POST",
      formData: true,
      body,
      setResponse: (response: any) => {
        setLoading(false);
        if (response.status === "success") {
          enqueueSnackbar('Usuario guardado con éxito', { variant: 'success' });
          setOpenEditor(false);
          loadList();
        } else {
          enqueueSnackbar(response.message || 'Error al guardar', { variant: 'error' });
        }
      },
      onError: (msg: string) => {
        setLoading(false);
        enqueueSnackbar(msg, { variant: 'error' });
      },
    });
  };






  return (
      <>
        <Dialog 
          open={openEditor}
          onClose={() => setOpenEditor(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <div className="DialogContainer">
              <div className="ModuleWrapper">
                  <div className="boxContainerHeader">
                      <div className="boxContainerTitle">
                          <h2 className="h2_title">Usuario</h2>
                          <span className="h2_subtitle">Editar</span>
                      </div>

                      
                  </div>

                  <div id="userForm">
                    <div className="userForm_right">
                      
                        <div className='input_group'>
                            <h2>Información Personal</h2>
                            <div className='input_group'>
                              <div className='input_row'>
                                <TextField 
                                  id="name"
                                  name="name"
                                  label="Nombres"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.name}
                                  onChange={handleInputChange}
                                />

                                <TextField
                                  id="lastname"
                                  name="lastname"
                                  label="Apellidos"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.lastname}
                                  onChange={handleInputChange}
                                />
                              </div>
                              <div className='input_row'>
                                <TextField
                                  id="phone"
                                  name="phone"
                                  label="Teléfono"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.phone}
                                  onChange={handleInputChange}
                                />

                                <TextField
                                  id="whatsapp"
                                  name="whatsapp"
                                  label="WhatsApp"
                                  type="text"
                                  fullWidth
                                  value={formValues.whatsapp}
                                  onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                                    setFormValues({ ...formValues, whatsapp: digits });
                                  }}
                                  inputProps={{ maxLength: 9 }}
                                  InputProps={{ startAdornment: <InputAdornment position="start">+56</InputAdornment> }}
                                  helperText="Solo 9 dígitos, sin +56. Ej: 912345678"
                                />
                              </div>

                            </div>

                            <div className='input_group'>
                              <h2>Información de sesión</h2>
                              <div className="input_row">
                                <TextField
                                  id="email"
                                  name="email"
                                  label="Email"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.email}
                                  onChange={handleInputChange}
                                />

                                <TextField
                                  id="password"
                                  name="password"
                                  label="Password"
                                  type="password"
                                  autoComplete="new-password"
                                  required
                                  fullWidth
                                  value={formValues.password}
                                  onChange={handleInputChange}
                                />
                              </div>



                            </div>

                            {formValues.role_id !== 1 && (
                              <div className='input_group'>
                                <h2>Información de Enfermera</h2>
                                <TextField
                                  id="rut"
                                  name="rut"
                                  label="RUT"
                                  type="text"
                                  fullWidth
                                  value={formValues.rut || ''}
                                  onChange={handleInputChange}
                                  helperText="Ej: 12.345.678-9"
                                  inputProps={{ maxLength: 12 }}
                                />
                                <AutocompleteField
                                  label="Región"
                                  endpoint="/locations/regiones/list"
                                  value={formValues.region_id}
                                  onChange={(data: any) => {
                                    setFormValues({ 
                                      ...formValues, 
                                      region_id: data.region_id
                                    });
                                  }}
                                />
                                {formValues.region_id && formValues.region_id !== '' && (
                                  <AutocompleteField
                                    label="Comunas"
                                    endpoint="/locations/comunas/list"
                                    endpoint_params={{ region_id: formValues.region_id }}
                                    multiple={true}
                                    value={formValues.locations}
                                    onChange={(data: any) => {
                                      setFormValues({ ...formValues, locations: data ? data : [] });
                                    }}
                                  />
                                )}
                                <AutocompleteField
                                  label="Habilidades"
                                  endpoint="/skills/list"
                                  multiple={true}
                                  value={formValues.skills}
                                  onChange={(data: any) => {
                                    setFormValues({ ...formValues, skills: data ? data : [] });
                                  }}
                                />
                                <div style={{ border: '2px solid #1976d2', borderRadius: '10px', padding: '12px 16px', background: '#f0f6ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div>
                                    <strong style={{ fontSize: '15px', color: '#1976d2' }}>Especialista en Heridas</strong>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#555' }}>
                                      Habilita procedimientos avanzados.
                                    </p>
                                  </div>
                                  <Switch
                                    checked={formValues.advanced_wound_care}
                                    onChange={(e) => setFormValues({ ...formValues, advanced_wound_care: e.target.checked })}
                                    color="primary"
                                  />
                                </div>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={formValues.online_advice}
                                      onChange={(e) => setFormValues({ ...formValues, online_advice: e.target.checked })}
                                    />
                                  }
                                  label="Asesoría en línea"
                                />
                                <TextField
                                  id="about_me"
                                  name="about_me"
                                  label="Sobre mí"
                                  type="text"
                                  fullWidth
                                  multiline
                                  value={formValues.about_me}
                                  onChange={handleInputChange}
                                />

                                {/* Domicilio y registro */}
                                <div className='input_row'>
                                  <TextField
                                    label="Comuna de residencia"
                                    value={formValues.home_location}
                                    onChange={(e) => setFormValues({ ...formValues, home_location: e.target.value })}
                                    fullWidth
                                    helperText="Para derivaciones rápidas"
                                  />
                                  <TextField
                                    label="Registro de la Superintendencia de Salud"
                                    value={formValues.sup_salud_registration}
                                    onChange={(e) => setFormValues({ ...formValues, sup_salud_registration: e.target.value })}
                                    fullWidth
                                  />
                                </div>

                                {/* Procedimientos */}
                                <div className='input_group'>
                                  <h2>Procedimientos y Valores Referenciales</h2>
                                  {(formValues.advanced_wound_care
                                    ? [...SPECIALIST_PROCEDURES, ...GENERAL_PROCEDURES.filter(p => p.skill_id !== 5)]
                                    : GENERAL_PROCEDURES
                                  ).map((proc, i, arr) => {
                                    const current = (formValues.treatments || buildDefaultTreatments())[proc.skill_id] || { active: false, price: '' };
                                    const isLast = i === arr.length - 1;
                                    return (
                                      <div key={proc.skill_id} style={{ paddingBottom: isLast ? 0 : '10px', marginBottom: isLast ? 0 : '10px', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                          <FormControlLabel
                                            style={{ minWidth: '230px', margin: 0 }}
                                            control={<Switch checked={!!current.active} onChange={(e) => updateTreatment(proc.skill_id, 'active', e.target.checked)} />}
                                            label={
                                              <div>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>{proc.label}</div>
                                                <div style={{ fontSize: 12, color: '#888' }}>{proc.description}</div>
                                              </div>
                                            }
                                          />
                                          {current.active && (
                                            <TextField
                                              label="Valor referencial ($)"
                                              value={current.price}
                                              onChange={(e) => updateTreatment(proc.skill_id, 'price', e.target.value.replace(/[^0-9]/g, ''))}
                                              size="small"
                                              style={{ width: '200px' }}
                                              inputProps={{ inputMode: 'numeric' }}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Plan de servicio */}
                                <div className='input_group'>
                                  <FormLabel style={{ fontWeight: 600 }}>Plan de servicio</FormLabel>
                                  <RadioGroup
                                    row
                                    value={formValues.plan_type || 'basico'}
                                    onChange={(e) => setFormValues({ ...formValues, plan_type: e.target.value })}
                                  >
                                    <FormControlLabel value="basico" control={<Radio />} label="Plan Básico" />
                                    <FormControlLabel value="preferente" control={<Radio />} label="Derivación Preferente" />
                                  </RadioGroup>

                                  {formValues.plan_type === 'preferente' && preferenteTerms && (
                                    <div style={{
                                      marginTop: '10px',
                                      padding: '14px 16px',
                                      background: '#fffbeb',
                                      border: '1px solid #fbbf24',
                                      borderRadius: '8px',
                                      fontSize: '13px',
                                      color: '#78350f',
                                      lineHeight: 1.6,
                                    }}>
                                      <div style={{ fontWeight: 600, marginBottom: '6px', color: '#92400e' }}>
                                        Condiciones del Plan Preferente
                                      </div>
                                      <div dangerouslySetInnerHTML={{ __html: preferenteTerms }} />
                                    </div>
                                  )}
                                </div>

                                {/* Certificados */}
                                <div className='input_group' style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, background: '#fafafa' }}>
                                  <h2 style={{ margin: '0 0 8px 0' }}>Certificados y Especializaciones</h2>
                                  <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#666' }}>Máximo 3 certificados (imagen o PDF).</p>

                                  {formValues.nurse_id ? (
                                    <>
                                      <div className='input_row'>
                                        <TextField
                                          label="Título del certificado"
                                          value={credTitle}
                                          onChange={(e) => setCredTitle(e.target.value)}
                                          fullWidth size="small"
                                        />
                                        <Button variant="outlined" component="label" size="small" sx={{ minWidth: 180, height: 40 }}>
                                          {credFile?.name ? credFile.name.slice(0, 18) + (credFile.name.length > 18 ? '…' : '') : 'Seleccionar archivo'}
                                          <input type="file" hidden accept="image/*,.pdf,application/pdf"
                                            onChange={(e: any) => { const f = e?.target?.files?.[0]; if (f) setCredFile(f); }} />
                                        </Button>
                                        <LoadingButton size="small" variant="contained" onClick={addCredential} loading={credLoading}
                                          disabled={(credentials?.length || 0) >= 3 || credLoading}>
                                          Agregar
                                        </LoadingButton>
                                        <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>{credentials?.length || 0}/3</span>
                                      </div>

                                      {credentials && credentials.length > 0 && (
                                        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                                          {credentials.map((c: any) => (
                                            <div key={c.credential_id} style={{ display: 'flex', gap: 12, alignItems: 'center', border: '1px solid #e6e6e6', borderRadius: 10, padding: 10, background: '#fff' }}>
                                              <div style={{ width: 64, height: 64, borderRadius: 8, overflow: 'hidden', background: '#f5f5f5', flex: '0 0 64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {c.image && (c.image.toLowerCase().endsWith('.pdf')
                                                  ? <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}><PdfThumb src={c.image} /></a>
                                                  : <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}><img src={c.image} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></a>
                                                )}
                                              </div>
                                              <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600 }}>{c.title}</div>
                                                <div style={{ fontSize: 12, color: '#999' }}>{c.created_at || ''}</div>
                                              </div>
                                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                <FormControlLabel
                                                  control={<Switch size="small" checked={!!c.is_visible} onChange={(e) => toggleVisibility(c.credential_id, e.target.checked)} />}
                                                  label={<span style={{ fontSize: 11, color: c.is_visible ? '#15803d' : '#94a3b8' }}>{c.is_visible ? 'Activo en perfil' : 'Oculto en perfil'}</span>}
                                                  labelPlacement="bottom" style={{ margin: 0 }}
                                                />
                                                <Button variant="text" color="error" size="small" onClick={() => deleteCredential(c.credential_id)}>Eliminar</Button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <p style={{ fontSize: 13, color: '#aaa' }}>Guarda primero el perfil de la enfermera para subir certificados.</p>
                                  )}
                                </div>

                              </div>
                            )}

                          



                          <div className='input_group'>
                            <h2>Estado de Registro</h2>
                            <FormControl fullWidth>
                              <InputLabel id="is_register_approved-label">Estado de Registro</InputLabel>
                              <Select
                                labelId="is_register_approved-label"
                                id="is_register_approved"
                                name="is_register_approved"
                                value={formValues.is_register_approved}
                                label="Estado de Registro"
                                onChange={handleInputChange}
                              >
                                <MenuItem value={0}>Pendiente</MenuItem>
                                <MenuItem value={1}>Aprobado</MenuItem>
                                <MenuItem value={2}>Rechazado</MenuItem>
                              </Select>
                            </FormControl>

                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formValues.is_active}
                                  onChange={(e) => setFormValues({ ...formValues, is_active: e.target.checked })}
                                />
                              }
                              label="Perfil Activo"
                            />
                          </div>



                        </div>


                      
                    </div>


                    

                </div>
              </div>
          </div>

          <div className="dialogButtons">
            <Button
                onClick={() => setOpenEditor(false)}
                variant="outlined"
                className="button_2"
                disabled={loading}
            >
              Cancelar
            </Button>

            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              className="button_1"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>

        </Dialog>
      </>
  );
}
